<script setup>
import { useCmsContentStore, useUsersStore } from '@/stores'
import { defaultSeedItems } from '@/utils/cmsSeedItems'
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'

const cmsStore = useCmsContentStore()
const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const itemsPerPage = 10

const searchValue = ref("")
const filtering = ref(false)
const currentPage = ref(1)
const orderBy = ref("key")
const orderDirection = ref("") // "" for asc, "-" for desc
const categoryFilter = ref("all")
const contentTypeFilter = ref("all")

const isShowingEditDialog = ref(false)
const isShowingNewDialog = ref(false)
const isConfirmDialogOpen = ref(false)
const selectedItem = ref(null)

const categories = [
  { text: 'All', value: 'all' },
  { text: 'Marketing', value: 'marketing' },
  { text: 'Navigation', value: 'navigation' },
  { text: 'Form', value: 'form' },
  { text: 'Action', value: 'action' },
  { text: 'Validation', value: 'validation' },
  { text: 'Filter', value: 'filter' },
  { text: 'Dialog', value: 'dialog' },
  { text: 'Empty State', value: 'empty-state' },
  { text: 'Help', value: 'help' },
  { text: 'Testimonial', value: 'testimonial' },
]

const contentTypes = [
  { text: 'All', value: 'all' },
  { text: 'Text', value: 'text' },
  { text: 'Rich Text', value: 'richtext' },
  { text: 'Block', value: 'block' },
]

const categoryColorMap = {
  marketing: 'primary',
  navigation: 'info',
  form: 'secondary',
  action: 'success',
  validation: 'error',
  filter: 'warning',
  dialog: 'indigo',
  'empty-state': 'grey',
  help: 'teal',
  testimonial: 'purple',
}

const dynamicSort = function(property) {
  var sortOrder = 1
  if (property[0] === "-") {
    sortOrder = -1
    property = property.substr(1)
  }

  return function (a, b) {
    let x = a[property] ? a[property] : ""
    let y = b[property] ? b[property] : ""
    var result = (x < y) ? -1 : (x > y) ? 1 : 0

    return result * sortOrder
  }
}

const filteredItems = computed(() => {
  filtering.value = true
  var returnArray = (cmsStore.contentItems || []).filter(() => true)

  if (categoryFilter.value !== 'all') {
    returnArray = returnArray.filter(item => item.category === categoryFilter.value)
  }

  if (contentTypeFilter.value !== 'all') {
    returnArray = returnArray.filter(item => item.contentType === contentTypeFilter.value)
  }

  if (searchValue.value != '' && searchValue.value) {
    const search = searchValue.value.toUpperCase()
    returnArray = returnArray.filter(item => {
      return item?.key?.toUpperCase()?.includes(search)
        || item?.displayName?.toUpperCase()?.includes(search)
        || item?.description?.toUpperCase()?.includes(search)
        || (typeof item?.value === 'string' && item?.value?.toUpperCase()?.includes(search))
    })
  }

  returnArray.sort(dynamicSort(orderDirection.value + orderBy.value))

  setTimeout(() => {
    filtering.value = false
  }, 100)

  return returnArray
})

const getValuePreview = item => {
  if (item.contentType === 'block' && Array.isArray(item.value)) {
    return `[${item.value.length} items]`
  }
  if (typeof item.value === 'string') {
    return item.value.length > 100 ? item.value.substring(0, 100) + '...' : item.value
  }
  
  return JSON.stringify(item.value).substring(0, 100)
}

const loadData = async () => {
  await cmsStore.getContentItems()

  // Also refresh public content so vue-i18n picks up changes without hard refresh
  await cmsStore.reloadContent()
}

const showEditDialog = async item => {
  selectedItem.value = await cmsStore.getContentItemById(item._id)
  isShowingEditDialog.value = true
}

const showDeleteDialog = item => {
  selectedItem.value = item
  isConfirmDialogOpen.value = true
}

const deleteItem = async id => {
  await cmsStore.deleteContentItem(id)
  await loadData()
}

const seedDefaults = async () => {
  try {
    const result = await cmsStore.seedContent(defaultSeedItems)
    alert(`Seeding complete: ${result.created} created, ${result.skipped} skipped`)
    await loadData()
  } catch (error) {
    alert('Seeding failed: ' + error.message)
  }
}

onMounted(async () => {
  await loadData()
})
</script>

<template>
  <div>
    <AdminEditCmsContentDialog
      v-model:is-dialog-visible="isShowingEditDialog"
      :content-item="selectedItem"
      @saved="loadData"
    />
    <AdminNewCmsContentDialog
      v-model:is-dialog-visible="isShowingNewDialog"
      @saved="loadData"
    />
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogOpen"
      :confirmation-msg="`Are you sure you want to delete <b>${selectedItem?.key}</b>?`"
      @confirm="deleteItem(selectedItem?._id)"
    />

    <!-- Back Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n2"
    >
      <div class="back-button-container">
        <RouterLink
          to="/admin/profile"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Restaurant Admin</span>
        </RouterLink>
      </div>
    </div>

    <div class="grid-sidebar-left content-wrapper mt-6">
      <!-- Sidebar -->
      <div>
        <VCard class="mb-4 pa-4 pt-8 pb-7">
          <div class="account-card">
            <div class="account-card-content">
              <div class="mb-2">
                Filters
              </div>
              <VSelect
                v-model="categoryFilter"
                class="mb-2"
                label="Category"
                item-title="text"
                item-value="value"
                :items="categories"
              />
              <VSelect
                v-model="contentTypeFilter"
                class="mb-2"
                label="Content Type"
                item-title="text"
                item-value="value"
                :items="contentTypes"
              />
              <VSelect
                v-model="orderBy"
                class="mb-2"
                label="Order By"
                item-title="text"
                item-value="value"
                :items="[
                  { text: 'Key', value: 'key' },
                  { text: 'Category', value: 'category' },
                  { text: 'Updated', value: 'updatedAt' },
                  { text: 'Created', value: 'createdAt' },
                ]"
              />
              <VRadioGroup
                v-model="orderDirection"
                row
              >
                <VRadio
                  label="A-Z"
                  value=""
                  density="compact"
                />
                <VRadio
                  label="Z-A"
                  value="-"
                  density="compact"
                />
              </VRadioGroup>

              <VBtn
                class="account-card-primary-action-button mb-2"
                block
                @click="isShowingNewDialog = true"
              >
                <VIcon
                  start
                  size="18"
                  icon="mdi-plus-circle-outline"
                />
                <span>New Content</span>
              </VBtn>

              <VBtn
                variant="outlined"
                block
                size="small"
                @click="seedDefaults"
              >
                <VIcon
                  start
                  size="18"
                  icon="mdi-database-import-outline"
                />
                <span>Seed Defaults</span>
              </VBtn>
            </div>
          </div>
        </VCard>
      </div>

      <!-- Main Content -->
      <div>
        <VCard class="pa-10 pt-8">
          <VRow>
            <h3 class="user-dashboard-card-title mb-1">
              CMS Content ({{ filteredItems.length }})
            </h3>
            <VSpacer />
            <VBtn
              color="grey-lighten-1"
              icon="mdi-refresh"
              variant="text"
              @click="loadData"
            />
          </VRow>
          <VTextField
            v-model="searchValue"
            :loading="filtering"
            density="compact"
            variant="solo"
            :label="(filtering ? 'Syncing... ' : 'Search by key, name, or content...')"
            append-inner-icon="mdi-magnify"
            single-line
            hide-details
            class="mb-4"
            clearable
            @keyup="currentPage = 1"
          />

          <div>
            <template v-for="(item, index) in filteredItems">
              <VCard
                v-if="((index >= (currentPage * itemsPerPage) - itemsPerPage) && (index < currentPage * itemsPerPage))"
                :key="item._id"
                class="mb-3 cms-item-card"
                @click="showEditDialog(item)"
              >
                <VCardTitle class="d-flex align-center flex-wrap pb-1">
                  <code class="text-caption mr-2">{{ item.key }}</code>
                  <VChip
                    :color="categoryColorMap[item.category] || 'grey'"
                    size="x-small"
                    variant="tonal"
                    class="mr-1"
                  >
                    {{ item.category }}
                  </VChip>
                  <VChip
                    v-if="item.contentType !== 'text'"
                    color="secondary"
                    size="x-small"
                    variant="tonal"
                  >
                    {{ item.contentType }}
                  </VChip>
                </VCardTitle>

                <VMenu>
                  <template #activator="{ props }">
                    <VBtn
                      density="compact"
                      class="cms-item-menu-button"
                      icon="mdi-dots-vertical"
                      v-bind="props"
                    />
                  </template>
                  <VList>
                    <VListItem @click="showEditDialog(item)">
                      <VListItemTitle>Edit</VListItemTitle>
                    </VListItem>
                    <VListItem @click="showDeleteDialog(item)">
                      <VListItemTitle>Delete</VListItemTitle>
                    </VListItem>
                  </VList>
                </VMenu>

                <VCardItem class="py-0">
                  <div
                    v-if="item.displayName"
                    class="text-body-2 font-weight-medium"
                  >
                    {{ item.displayName }}
                  </div>
                  <div class="text-caption text-medium-emphasis mt-1">
                    {{ getValuePreview(item) }}
                  </div>
                  <div
                    v-if="item.description"
                    class="text-caption text-disabled mt-1"
                  >
                    {{ item.description }}
                  </div>
                </VCardItem>

                <VCardItem class="py-1">
                  <div class="text-caption text-disabled">
                    v{{ item.version }}
                  </div>
                </VCardItem>
              </VCard>
            </template>

            <VPagination
              v-model="currentPage"
              :length="Math.ceil(filteredItems.length / itemsPerPage)"
            />
          </div>
        </VCard>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cms-item-card {
  cursor: pointer;
  position: relative;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.5);
  }
}

.cms-item-menu-button {
  position: absolute;
  float: right;
  top: 0;
  right: 0;
  margin: 20px;
}

.back-button-container {
  padding: 0 4px;
}

.back-button {
  padding: 8px 12px;
  color: rgb(51, 51, 51);
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 4px;
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: rgb(102, 102, 102);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
